
import type React from "react"

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Payment, Installment } from "@/api/payments"
import { useToast } from "@/hooks/use-toast"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { InstallmentInput } from "./installment-input"

interface AddInstallmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  payment: Payment
  onInstallmentAdded: (paymentId: string, installments: Installment[]) => void
}

export function AddInstallmentDialog({ open, onOpenChange, payment, onInstallmentAdded }: AddInstallmentDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [installments, setInstallments] = useState<Installment[]>([])
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({})

  // Initialize installments when the dialog opens or payment changes
  useEffect(() => {
    if (open && payment) {
      setInstallments(payment.installments || [])
    }
  }, [open, payment])

  const validateForm = () => {
    const errors: { [key: string]: string } = {}

    const invalidInstallments = installments.some(
      (inst) => !inst.amount || isNaN(Number(inst.amount)) || Number(inst.amount) <= 0,
    )

    if (invalidInstallments) {
      errors.installments = t("payments.allInstallmentAmounts", "All installment amounts must be valid")
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validateForm()) {
      toast({
        title: t("common.error"),
        description: t("common.pleaseFixErrors"),
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)

    try {
      onInstallmentAdded(payment._id || "", installments)
    } catch (error) {
      console.error("Error adding installment:", error)
      toast({
        title: t("common.error"),
        description: t("common.errorOccurred"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Get user name from payment
  const getUserName = () => {
    if (typeof payment.userId === "object" && payment.userId !== null) {
      const user = payment.userId
      return `${user.name} ${user.surname || ""}`.trim()
    }
    return "Unknown User"
  }

  // Get plan name from payment
  const getPlanName = () => {
    if (typeof payment.planId === "object" && payment.planId !== null) {
      return payment.planId.name
    }
    return "Unknown Plan"
  }

  // Get plan price
  const getPlanPrice = () => {
    if (typeof payment.planId === "object" && payment.planId !== null) {
      return payment.planId.price || 0
    }
    return payment.amount || 0
  }

  // Calculate total paid amount
  const getPaidAmount = () => {
    return installments.reduce((sum, inst) => sum + Number(inst.amount), 0)
  }

  // Calculate remaining amount
  const getRemainingAmount = () => {
    const planPrice = getPlanPrice()
    const paidAmount = getPaidAmount()
    return Math.max(0, planPrice - paidAmount)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t("payments.addInstallment", "Add Installment")}</DialogTitle>
          <DialogDescription>
            {t("payments.addInstallmentDescription", "Add a new installment to this payment")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Payment Info */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.user", "User")}</Label>
                <div className="font-medium">{getUserName()}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.plan", "Plan")}</Label>
                <div className="font-medium">{getPlanName()}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.totalAmount", "Total Amount")}</Label>
                <div className="font-medium">${getPlanPrice().toFixed(2)}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">{t("payments.paidAmount", "Paid Amount")}</Label>
                <div className="font-medium">${getPaidAmount().toFixed(2)}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">
                  {t("payments.remainingAmount", "Remaining Amount")}
                </Label>
                <div className="font-medium">${getRemainingAmount().toFixed(2)}</div>
              </div>
            </div>

            {/* Installments */}
            <div className="col-span-4">
              <InstallmentInput
                value={installments}
                onChange={setInstallments}
                suggestedAmount={getRemainingAmount()}
              />
              {formErrors.installments && <p className="text-sm text-red-500 mt-1">{formErrors.installments}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
